import { provideHttpClient } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { PrimeNG, providePrimeNG } from 'primeng/config';

import {
  HTTP_INTERCEPTORS,
  HttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';

import { AuthInterceptor } from '@/core/interceptors/auth.interceptor';
import { AppTitleService } from '@/core/services/app-title/app-title.service';
import {
  readStoredWhitelabelFavicon,
  readStoredWhitelabelOrigin,
  readStoredWhitelabelSlug,
  storeWhitelabelFavicon,
} from '@/core/services/whitelabel/whitelabel-storage';
import { WhitelabelService } from '@/core/services/whitelabel/whitelabel.service';
import { CAMERITE_THEME_LIGTH } from '@/core/theme/tokens';
import { HomeState } from '@/modules/home/core/state/home.state';
import { HorusState } from '@/modules/horus/core/state/horus.state';
import { IotDevicesState } from '@/modules/automation/core/state/iot-device.state';
import { AutomationRulesState } from '@/modules/automation/core/state/automation-rule.state';

import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';

import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';
import { firstValueFrom } from 'rxjs';
import { PRIMENG_TRANSLATIONS } from '../../public/i18n/primeng';
import { appRoutes } from '../app.routes';
import { environment } from '../environments/environment';

const DEFAULT_LANG = 'pt';

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (
  http: HttpClient,
) => new TranslateHttpLoader(http, '/i18n/locales/', '.i18n.json');

const appInitializeFactory = (
  translate: TranslateService,
  primeConfig: PrimeNG,
) => {
  return async () => {
    translate.setDefaultLang(DEFAULT_LANG);
    const browserLang = translate.getBrowserLang();
    const validLang = ['pt', 'en', 'es'].includes(browserLang ?? '')
      ? browserLang
      : DEFAULT_LANG;

    await firstValueFrom(translate.use(validLang ?? DEFAULT_LANG));

    primeConfig.setTranslation(
      PRIMENG_TRANSLATIONS[validLang as keyof typeof PRIMENG_TRANSLATIONS],
    );
    translate.onLangChange.subscribe((event) => {
      const newLang = event.lang as keyof typeof PRIMENG_TRANSLATIONS;
      if (PRIMENG_TRANSLATIONS[newLang]) {
        primeConfig.setTranslation(PRIMENG_TRANSLATIONS[newLang]);
      }
    });
  };
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'top',
      }),
      withEnabledBlockingInitialNavigation(),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),

    providePrimeNG({
      theme: {
        preset: CAMERITE_THEME_LIGTH,
        options: { darkModeSelector: '.app-light' },
      },
      ripple: true,
      translation: PRIMENG_TRANSLATIONS[DEFAULT_LANG],
    }),
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ]),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializeFactory,
      deps: [TranslateService, PrimeNG],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const whitelabelService = inject(WhitelabelService);
        const appTitleService = inject(AppTitleService);

        return async () => {
          seedWhitelabelFavicon();

          try {
            const origin =
              readStoredWhitelabelOrigin() ?? window.location.hostname;
            const slug = readStoredWhitelabelSlug() ?? undefined;

            const config = await firstValueFrom(
              whitelabelService.getPublicConfigurations(origin, slug),
            );
            const root = document.documentElement;

            if (config.color1) {
              applyWhitelabelColors(root, config.color1);
              defaultColor = config.color1;
            } else {
              applyWhitelabelColors(root, defaultColor);
            }

            appTitleService.setDomainConfig(config);

            const faviconUrl = config.files?.['favicon.ico'];
            if (faviconUrl) {
              defaultFavicon = faviconUrl;
            }
            applyWhitelabelFavicon(faviconUrl);
            storeWhitelabelFavicon(config);
          } catch {
            applyWhitelabelColors(document.documentElement, defaultColor);
          }
        };
      },
      deps: [],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    provideStore([
      HomeState,
      HorusState,
      IotDevicesState,
      AutomationRulesState,
    ]),
    withNgxsReduxDevtoolsPlugin(),
    provideEchartsCore({ echarts }),
    ...environment.providers,
  ],
};

export let defaultColor = '#52309C';
export let defaultFavicon =
  document.querySelector<HTMLLinkElement>("link[rel~='icon']")?.href ??
  'favicon.ico';

export function applyWhitelabelFavicon(
  faviconUrl?: string | null,
  options?: { cacheBust?: boolean },
) {
  const href = faviconUrl || defaultFavicon;
  const cacheBust = options?.cacheBust ?? true;

  document
    .querySelectorAll("link[rel~='icon']")
    .forEach((link) => link.remove());

  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/x-icon';
  link.href =
    cacheBust && /^https?:/.test(href)
      ? `${href}${href.includes('?') ? '&' : '?'}v=${Date.now()}`
      : href;
  document.head.appendChild(link);
}

function seedWhitelabelFavicon() {
  const storedFavicon = readStoredWhitelabelFavicon();
  if (!storedFavicon) return;

  applyWhitelabelFavicon(storedFavicon, { cacheBust: false });
}

export function applyWhitelabelColors(root: HTMLElement, color1: string) {
  let normalized = color1.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((c) => c + c)
      .join('');
  } else if (normalized.length === 4) {
    normalized = normalized
      .split('')
      .map((c) => c + c)
      .join('');
  }

  const color1Normalized = '#' + normalized.slice(0, 6);

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const textColor = luminance > 0.5 ? '#1a1a1a' : '#ffffff';

  root.style.setProperty('--p-primary-color', color1Normalized);
  root.style.setProperty('--p-primary-contrast-color', textColor);
  root.style.setProperty('--d-menuitem-text-color', textColor);
  root.style.setProperty('--d-menuitem-text-hover-color', textColor);
  root.style.setProperty('--d-menuitem-active-text-color', textColor);

  root.style.setProperty('--sidebar-text', textColor);
  root.style.setProperty(
    '--sidebar-button-border',
    luminance > 0.5 ? 'rgba(0,0,0,0.20)' : 'transparent',
  );
  root.style.setProperty('--sidebar-icon-invert', luminance > 0.5 ? '0' : '1');
  root.style.setProperty(
    '--sidebar-text-85',
    luminance > 0.5 ? 'rgba(26,26,26,0.85)' : 'rgba(255,255,255,0.85)',
  );
  root.style.setProperty(
    '--sidebar-text-65',
    luminance > 0.5 ? 'rgba(26,26,26,0.65)' : 'rgba(255,255,255,0.65)',
  );
  root.style.setProperty(
    '--sidebar-hover-bg',
    luminance > 0.5 ? 'rgba(26,26,26,0.10)' : 'rgba(255,255,255,0.15)',
  );
  root.style.setProperty(
    '--sidebar-active-bg',
    luminance > 0.5 ? 'rgba(26,26,26,0.15)' : 'rgba(255,255,255,0.20)',
  );

  root.style.setProperty('--p-button-primary-background', color1Normalized);
  root.style.setProperty('--p-button-primary-border-color', color1Normalized);
  root.style.setProperty('--p-button-primary-color', textColor);

  const hoverColor = darken(color1Normalized, 10);
  root.style.setProperty('--p-button-primary-hover-background', hoverColor);
  root.style.setProperty('--p-button-primary-hover-border-color', hoverColor);
  root.style.setProperty('--p-button-primary-hover-color', textColor);

  const activeColor = darken(color1Normalized, 18);
  root.style.setProperty('--p-button-primary-active-background', activeColor);
  root.style.setProperty('--p-button-primary-active-border-color', activeColor);
  root.style.setProperty('--p-button-primary-active-color', textColor);

  root.style.setProperty('--p-badge-primary-background', color1Normalized);
  root.style.setProperty('--p-badge-primary-color', textColor);
  root.style.setProperty('--p-tag-primary-background', color1Normalized);
  root.style.setProperty('--p-tag-primary-color', textColor);

  root.style.setProperty('--p-highlight-background', color1Normalized);
  root.style.setProperty('--p-highlight-color', textColor);

  root.style.setProperty('--p-checkbox-checked-background', color1Normalized);
  root.style.setProperty('--p-checkbox-checked-border-color', color1Normalized);
  root.style.setProperty('--p-checkbox-checked-color', textColor);
  root.style.setProperty(
    '--p-radiobutton-checked-background',
    color1Normalized,
  );
  root.style.setProperty(
    '--p-radiobutton-checked-border-color',
    color1Normalized,
  );
  root.style.setProperty(
    '--p-toggleswitch-checked-background',
    color1Normalized,
  );

  root.style.setProperty('--p-steps-item-active-color', color1Normalized);
  root.style.setProperty('--p-stepper-step-active-color', color1Normalized);

  root.style.setProperty('--p-tabs-tab-active-color', color1Normalized);
  root.style.setProperty('--p-tabs-tab-active-border-color', color1Normalized);

  root.style.setProperty('--p-primary-hover-color', hoverColor);
}

function darken(hex: string, percent: number): string {
  const clean = hex.replace('#', '');
  let r = parseInt(clean.slice(0, 2), 16);
  let g = parseInt(clean.slice(2, 4), 16);
  let b = parseInt(clean.slice(4, 6), 16);

  r = Math.max(0, Math.floor(r * (1 - percent / 100)));
  g = Math.max(0, Math.floor(g * (1 - percent / 100)));
  b = Math.max(0, Math.floor(b * (1 - percent / 100)));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
